<style scoped lang="scss">
    .modal-box {
        background-color: #fff;
    }
    form {
        h2 {
            color: #000;
        }
        ::v-deep(.formkit-label), ::v-deep(legend) {
            color: #000 !important;
            font-weight: 400;
        }
        ::v-deep(button[type=submit]), .closeModalDialog {
            text-transform: uppercase;
            font-weight: 400;
            font-size: 14px;
        }
    }
</style>
<template>
    <dialog ref="HTMLDialogElement" :id="transporterFormModalId" class="modal modal-middle sm:modal-middle">
        <div class="modal-box" method="dialog">
            <FormKit type="form" :id="transporterFormModalId" @submit="submitHandler" :plugins="[zodPlugin]"
                :actions="false" incomplete-message="Désolé, tous les champs ne sont pas remplis correctement."
                #default="{ state: { valid } }">
                <h2>{{ modalTitle }}</h2>
                <hr />
                <div class="grid grid-cols-2">
                    <div class="flex justify-center items-center">
                        <UploadImage :image-preview-url="transporter.vignette
                            ? transporter.vignette.url
                            : 'media/avatars/blank2.png'"
                            @image-uploaded="onImageUploaded"
                            @image-removed="onImageRemoved"
                            @error="onUploadImgError($event)"
                        />
                    </div>
                    <div class="flex justify-center items-center">
                        <FormKit
                            v-model="transporter.name"
                            type="text"
                            name="name"
                            label="Nom du transporteur"
                        />
                    </div>
                </div>
                <div class="upload-image-error text-xs text-red-500">
                    <span>{{ uploadImgErrorMsg }}</span>
                </div>
                <div class="modal-action">
                    <button type="button" class="btn closeModalDialog text-white" @click="closeModalDialog()">Annuler</button>
                    <FormKit type="submit" :label="transporterUuid ? 'Modifier' : 'Créer'" :disabled="!valid" />
                </div>
            </FormKit>
        </div>
    </dialog>
</template>

<script setup lang="ts">
import UploadImage from "@/components/forms/UploadImage.vue";
import {
    fetchTransporterByUuid,
    updateTransporter,
    createTransporter,
} from "@/_services/transporter/transporter";
import { ref, watch, toRef, onMounted } from "vue";
import { ITransporterList } from "@/models/transporter/ITransporterList";
import { z } from "zod";
import { createZodPlugin } from "@formkit/zod";
import swal from "@/_helpers/swal";

const transporter = ref<ITransporterList>({
    name: "",
    isActive: true,
    vignette: null,
    uuid: "",
} as unknown as ITransporterList);
const props = defineProps({
    transporterUuid: {
        type: String,
        required: false,
        default: "",
    },
    transporterFormModalId: {
        type: String,
        required: true,
    },
});
const emit = defineEmits(["transporterCreated", "transporterUpdated"]);
const uploadImgErrorMsg = ref<string>("");
const modalHTMLElement = ref<HTMLDialogElement | null>(null);
const modalDialogState = ref<"open" | "close">("close");
const transporterUuid = toRef(props, "transporterUuid");
const transporterFormModalId = toRef(props, "transporterFormModalId");
const modalTitle = transporterUuid.value
    ? ref<string>("Editer un transporteur")
    : ref<string>("Créer un transporteur");

onMounted(() => {
    modalHTMLElement.value = document.querySelector(
        `#${transporterFormModalId.value}`
    );
    if (modalHTMLElement.value) {
        observer.observe(modalHTMLElement.value, { attributes: true });
    }
});

const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
        if (mutation.attributeName === "open" && modalHTMLElement.value) {
            const isOpen = modalHTMLElement.value.open;
            const isclosed = !isOpen;
            modalDialogState.value = isOpen ? "open" : "close";
            if (transporterUuid.value && isclosed) {
                updateTransporterFromApi(transporterUuid.value as string);
            }
            if (
                (!transporterUuid.value && isclosed) ||
                (!transporterUuid.value && isOpen)
            ) {
                cleanForm();
            }

            break; // Exit the loop once the class attribute has been processed
        }
    }
});

const onImageUploaded = (image) => {
    transporter.value.vignette = {
        name: image.name ?? "",
        url: image.url,
        uuid: image.uuid,
    };
};

const onImageRemoved = () => {
    transporter.value.vignette = null;
};

const onUploadImgError = (errorMsg: string) => {
    uploadImgErrorMsg.value = errorMsg;
    console.dir(errorMsg);
};

const zodSchema = z.object({
    name: z.string()
        .trim()
        .nonempty("Le champs Nom du transporteur est obligatoire")
        .min(1, { message: "Le champs Nom du fournisseur est obligatoire" })
        .max(50, { message: "Le nom du fournisseur ne doit pas dépasser 50 caractères" })
        .regex(
            /^[a-zA-Z0-9_\sçé]*$/,
            "Le nom du transporteur ne doit pas contenir de caractères spéciaux"
        )
});

const [zodPlugin, submitHandler] = createZodPlugin(
    zodSchema,
    async (formData) => {
        try {
            closeModalDialog();
            const data = {
                name: formData.name,
                isActive: transporter.value.isActive,
                vignette: transporter.value.vignette
                    ? "/api/images/" + transporter.value.vignette.uuid
                    : null,
                uuid: transporter.value.uuid,
            };
            const isEdit = Boolean(transporterUuid.value);
            const title = isEdit
                ? "Êtes-vous sûr de vouloir éditer le transporteur"
                : "Êtes-vous sûr de vouloir créer le transporteur";
            const confirmText = isEdit ? "Oui, éditer" : "Oui, créer";
            await swal.confirm(title, "Annuler", confirmText).then(async (result) => {
                if (result.isConfirmed) {
                    if (isEdit) {
                        await updateTransporter(transporterUuid.value as string, data);
                        await swal.success("Transporteur édité avec succès", "");
                        emit("transporterUpdated");
                    } else {
                        await createTransporter(data);
                        await swal.info("Transporteur créé avec succès");
                        emit("transporterCreated");
                        cleanForm();
                    }
                }
            });
        } catch (err: any) {
            swal.error(err.errors[0]);
        }
    }
)

const cleanForm = () => {
    transporter.value = {
        name: "",
        isActive: true,
        vignette: null,
        uuid: "",
    } as unknown as ITransporterList;
};

const closeModalDialog = () => {
    if (modalHTMLElement.value) {
        modalHTMLElement.value.close();
    }
};

watch(
    () => transporterUuid.value,
    (newValue) => {
        if (newValue) {
            modalTitle.value = "Editer un transporteur";
            fetchTransporterByUuid(newValue).then((response) => {
                transporter.value = response.data;
            });
        } else {
            modalTitle.value = "Créer un transporteur";
        }
    }
);
watch(
    () => transporter.value.name,
    (newValue) => {
        if (newValue) {
            transporter.value.name = newValue.trimStart();
        }
    }
);

const updateTransporterFromApi = (uuid: string) => {
    fetchTransporterByUuid(uuid).then((response) => {
        transporter.value = response.data;
    });
};
</script>  